'use no memo';

import {Alpha3CountryCode, DataStatus, Label, Search, useAnimatedPopper} from 'platform/components';
import {Box, Integer, Scroll, Spinner, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {isEmpty} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import {
  CustomerAdvancedSearchResultResponseBody,
  CustomerResponseBodyV2,
  IdentityCardResponseBodyV2,
  PersonResponseBodyV2,
  useCustomerAdvancedSearchQuery,
  useLazyGetCustomerV2Query,
} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {Nullish, TestIdProps, useDebouncedCallback} from 'shared/web';

import {getCustomerName} from '../../utils/getCustomerName';
import {getNaturalPersonFullName} from '../../utils/getNaturalPersonFullName';
import {handleApiError} from '../../utils/handleApiError';
import {CustomerSearchResultItem} from './components/CustomerSearchResultItem';
import {getCustomerSearchRowKey, getCustomerSearchRows} from './utils/getCustomerSearchRows';
import {getFormPhoneValue} from './utils/getFormPhoneValue';

/**
 * The selected entity returned by `onCustomerSelect`.
 *
 * A selection always carries data about **two** entities: the customer/company
 * and a specific person within it. Which person depends on the clicked row:
 * - **Contact person row** → the clicked contact.
 * - **Customer / company row** → the founding person, or the first contact as a fallback.
 *
 * Fields are split accordingly — `customerId` + `name` identify the customer,
 * while `id` + the remaining person fields describe that person.
 */
export interface SaneCustomer {
  /** ID of the selected **person** (the contact person, or the customer's founding person / first contact). */
  id: string;
  /** ID of the **customer/company** the selected person belongs to. */
  customerId: string;
  /** Display name of the **customer/company** (business trade name, or the founding person's full name for a natural person). */
  name: string;
  /** Full name of the selected **person** (the contact person, or the founding person / first contact). */
  contactPersonName: string;
  /** Email of the selected **person** (empty string when none). */
  email: string;
  /** Phone number of the selected **person**, falling back to the tenant's public phone number. */
  phoneNumber: string;
  /** Dial prefix matching `phoneNumber`. */
  prefix: string;
  /** Country code matching `phoneNumber`. */
  countryCode: Alpha3CountryCode;
  /** Identity cards of the selected **person**. */
  identityCards: IdentityCardResponseBodyV2[];
  /**
   * The customer's **primary contact** — the founding person for a natural person,
   * or the designated primary contact for a company. Resolved from the full customer
   * record by matching the advanced-search result's primary contact. `null` when the
   * customer has no primary contact (or it could not be matched).
   */
  primaryContact: SanePrimaryContact | null;
}

/**
 * The customer's primary contact, returned alongside the selected person on
 * `SaneCustomer.primaryContact`. Mirrors the person-level fields of {@link SaneCustomer}
 * so it can be consumed in the same way.
 */
export interface SanePrimaryContact {
  /** ID of the primary contact person. */
  id: string;
  /** Full name of the primary contact. */
  name: string;
  /** Email of the primary contact (empty string when none). */
  email: string;
  /** Phone number of the primary contact (empty string when none). */
  phoneNumber: string;
  /** Dial prefix matching `phoneNumber`. */
  prefix: string;
  /** Country code matching `phoneNumber`. */
  countryCode: Alpha3CountryCode;
  /** Identity cards of the primary contact. */
  identityCards: IdentityCardResponseBodyV2[];
}

interface CustomerSelectSearchProps extends TestIdProps {
  onCustomerSelect: (saneCustomer: SaneCustomer) => void;
  onAfterCustomerSelect?: () => void;
  defaultSearchString?: string | Nullish;
  customPopperWidth?: number;
  isDisabled?: boolean;
}

/**
 * Resolves the customer's primary contact into a {@link SanePrimaryContact}.
 *
 * The advanced-search result only carries the primary contact's id, so we match it
 * against the full customer record (founding person + contacts) to recover the
 * structured phone, email and identity-card data. Returns `null` when there is no
 * primary contact or it cannot be matched.
 */
const resolvePrimaryContact = (
  customer: CustomerResponseBodyV2,
  primaryContactId: string | Nullish
): SanePrimaryContact | null => {
  if (!primaryContactId) {
    return null;
  }

  const candidates: (PersonResponseBodyV2 | null)[] = [
    customer.foundingPerson,
    ...customer.contacts,
  ];
  const person = candidates.find((candidate) => candidate?.id === primaryContactId);

  if (!person) {
    return null;
  }

  const phoneNumber = person.phoneNumbers?.[0];

  return {
    id: person.id,
    name: getNaturalPersonFullName(person) ?? '',
    email: person.emails?.[0]?.email || '',
    phoneNumber: getFormPhoneValue(phoneNumber?.number),
    prefix: getFormPhoneValue(phoneNumber?.prefix),
    countryCode: phoneNumber?.countryCode as Alpha3CountryCode,
    identityCards: person.identityCards,
  };
};

const DEFAULT_POPPER_WIDTH_PX = 560;
const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;
const POPPER_GUTTER_PX = 10;
const RESULTS_MAX_HEIGHT = 100;
const PX_PER_DESIGN_STEP = 4;

export function CustomerSelectSearch(props: CustomerSelectSearchProps) {
  const [getCustomer] = useLazyGetCustomerV2Query();
  const {data: tenant} = useGetTenantQuery();
  const searchRef = useRef<null | HTMLDivElement>(null);

  const [searchString, setSearchString] = useState<string | null>(
    props.defaultSearchString ?? null
  );
  const [debouncedSearchString, setDebouncedSearchString] = useState<string | null>(null);

  const {data: matchingCustomers, isFetching: isFetchingCustomers} = useCustomerAdvancedSearchQuery(
    {name: debouncedSearchString},
    {
      skip: (debouncedSearchString?.length ?? 0) < SEARCH_MIN_LENGTH,
      refetchOnMountOrArgChange: true,
    }
  );

  const {openPopper, referenceRef, popperProps, Popper, closePopper} = useAnimatedPopper({
    placement: 'bottom-start',
    strategy: 'fixed',
    gutter: POPPER_GUTTER_PX,
  });

  const updateDebouncedSearchString = useDebouncedCallback(
    (val: string | null) => setDebouncedSearchString(val),
    SEARCH_DEBOUNCE_MS
  );

  const handleOnChange = (val: string | null) => {
    setSearchString(val);
    updateDebouncedSearchString(val);
    openPopper();
  };

  const handleSelectCustomer = (
    customer: CustomerAdvancedSearchResultResponseBody,
    contactId?: string
  ) => {
    getCustomer({customerId: customer.id})
      .unwrap()
      .then((fetchedCustomer) => {
        const pickedContact = contactId
          ? fetchedCustomer?.contacts?.find((c) => c.id === contactId)
          : undefined;
        const customerInfo =
          pickedContact ?? fetchedCustomer?.foundingPerson ?? fetchedCustomer?.contacts?.[0];

        if (!customerInfo) {
          return;
        }

        const email = customerInfo.emails?.[0]?.email;
        const customerPhoneNumber = customerInfo.phoneNumbers?.[0];
        const phoneNumber = customerPhoneNumber ?? tenant?.publicContacts?.phoneNumber;
        const name = getCustomerName(fetchedCustomer);

        props.onCustomerSelect({
          id: customerInfo.id,
          customerId: fetchedCustomer.id,
          name: name ?? '',
          contactPersonName: getNaturalPersonFullName(customerInfo) ?? '',
          email: email || '',
          phoneNumber: getFormPhoneValue(phoneNumber?.number),
          prefix: getFormPhoneValue(phoneNumber?.prefix),
          countryCode: phoneNumber?.countryCode as Alpha3CountryCode,
          identityCards: customerInfo.identityCards,
          primaryContact: resolvePrimaryContact(fetchedCustomer, customer.primaryContact?.id),
        });

        setSearchString(name ?? '');

        closePopper();
        props.onAfterCustomerSelect?.();
      })
      .catch(handleApiError);
  };

  const popperWidth = Math.floor(
    // eslint-disable-next-line react-hooks/refs
    (props.customPopperWidth ?? searchRef.current?.clientWidth ?? DEFAULT_POPPER_WIDTH_PX) /
      PX_PER_DESIGN_STEP
  ) as Integer;

  const searchRows = getCustomerSearchRows(matchingCustomers);

  return (
    <>
      <Box ref={referenceRef}>
        <VStack>
          <Label>{i18n.t('entity.document.actions.searchForCustomer')}</Label>
          <Box ref={searchRef}>
            <Search
              value={searchString}
              onChange={handleOnChange}
              onFocus={openPopper}
              onClear={closePopper}
              label={i18n.t('general.labels.searchPlaceholder')}
              isRequired
              isDisabled={props.isDisabled}
            />
          </Box>
        </VStack>
      </Box>
      <Popper {...popperProps}>
        <Box
          width={popperWidth}
          backgroundColor="general.white"
          borderRadius="medium"
          boxShadow="elevation_2"
          overflow="hidden"
        >
          <Box
            paddingHorizontal={4}
            paddingVertical={3}
            borderBottom="1px solid"
            borderColor="general.separator"
          >
            {match(isFetchingCustomers)
              .with(true, () => <Spinner size="small" />)
              .with(false, () => (
                <Text size="small" color="secondary">
                  {i18n.t('entity.customer.labels.resultsFound', {count: searchRows.length})}
                </Text>
              ))
              .exhaustive()}
          </Box>
          <DataStatus
            isLoading={isFetchingCustomers}
            spacing={20}
            isEmpty={isEmpty(searchRows)}
            emptyMessage={i18n.t('entity.customer.labels.noResults')}
          >
            <Scroll auto maxHeight={RESULTS_MAX_HEIGHT}>
              {searchRows.map((row) => (
                <CustomerSearchResultItem
                  key={getCustomerSearchRowKey(row)}
                  row={row}
                  onSelect={handleSelectCustomer}
                  data-testid={props['data-testid']}
                />
              ))}
            </Scroll>
          </DataStatus>
        </Box>
      </Popper>
    </>
  );
}
